// EditProfile.jsx avec palette noire/indigo

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import Navbar from '../components/NavBar';
import { Save, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function EditProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  
  // États du formulaire
  const [formData, setFormData] = useState({
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    email: user?.email || '',
    ville: user?.ville || '',
    bio: user?.bio || '',
    niveauCourse: user?.niveauCourse || 'Non spécifié',
    objectifs: user?.objectifs || ''
  });
  
  const [profilePhoto, setProfilePhoto] = useState(user?.photoProfil || null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Callback quand la photo est mise à jour
  const handlePhotoUpdate = (newPhotoUrl) => {
    setProfilePhoto(newPhotoUrl);
    
    // Mettre à jour le contexte utilisateur immédiatement
    setUser({
      ...user,
      photoProfil: newPhotoUrl
    });
    
    console.log('Photo mise à jour dans le contexte');
  };

  // Gérer les changements du formulaire
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Sauvegarder les modifications (sauf la photo qui est déjà sauvegardée)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.put('/users/profile', formData);
      
      // Mettre à jour le contexte utilisateur
      setUser({
        ...user,
        ...formData,
        photoProfil: profilePhoto
      });

      setMessage({
        type: 'success',
        text: 'Profil mis à jour avec succès !'
      });

      // Rediriger vers le profil après 1.5 secondes
      setTimeout(() => {
        navigate('/profile');
      }, 1500);
    } catch (error) {
      console.error('Erreur mise à jour profil:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Erreur lors de la mise à jour'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/profile')}
            className="mr-4 p-2 hover:bg-slate-900 border border-slate-800 text-gray-300 hover:text-white rounded-lg transition"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold text-white">
            Modifier mon profil
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ============================================ */}
          {/* SECTION PHOTO DE PROFIL */}
          {/* ============================================ */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-6">
              Photo de profil
            </h2>
            
            <ProfilePhotoUpload
              currentPhoto={profilePhoto}
              onPhotoUpdate={handlePhotoUpdate}
            />
          </div>

          {/* ============================================ */}
          {/* SECTION INFORMATIONS PERSONNELLES */}
          {/* ============================================ */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-6">
              Informations personnelles
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Prénom */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                />
              </div>

              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                />
              </div>

              {/* Email */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                />
              </div>

              {/* Ville */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ville
                </label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  placeholder="Ex: Montréal, QC"
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500"
                />
              </div>
            </div>
          </div>

          {/* ============================================ */}
          {/* SECTION RUNNING */}
          {/* ============================================ */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-6">
              Profil de coureur
            </h2>
            
            {/* Niveau */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Niveau de course
              </label>
              <select
                name="niveauCourse"
                value={formData.niveauCourse}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="Non spécifié">Non spécifié</option>
                <option value="Débutant">Débutant</option>
                <option value="Intermédiaire">Intermédiaire</option>
                <option value="Avancé">Avancé</option>
              </select>
            </div>

            {/* Bio */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Bio
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows="4"
                maxLength="500"
                placeholder="Parle un peu de toi et de ta passion pour la course..."
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none placeholder-gray-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                {formData.bio.length}/500 caractères
              </p>
            </div>

            {/* Objectifs */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Objectifs
              </label>
              <textarea
                name="objectifs"
                value={formData.objectifs}
                onChange={handleChange}
                rows="3"
                maxLength="300"
                placeholder="Tes objectifs de course (ex: Courir un marathon, améliorer ton temps...)"
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none placeholder-gray-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                {formData.objectifs.length}/300 caractères
              </p>
            </div>
          </div>

          {/* ============================================ */}
          {/* MESSAGE DE FEEDBACK */}
          {/* ============================================ */}
          {message.text && (
            <div className={`p-4 rounded-lg border ${
              message.type === 'success' 
                ? 'bg-green-900/20 border-green-500 text-green-400' 
                : 'bg-red-900/20 border-red-500 text-red-400'
            }`}>
              {message.type === 'success' ? '✅' : '❌'} {message.text}
            </div>
          )}

          {/* ============================================ */}
          {/* BOUTONS D'ACTION */}
          {/* ============================================ */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="flex-1 px-6 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-white rounded-lg font-semibold transition"
            >
              Annuler
            </button>
            
            <button
              type="submit"
              disabled={loading}
              className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold transition shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save size={20} />
                  <span>Enregistrer les modifications</span>
                </>
              )}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default EditProfile;